import { useBreakpointDown } from '@all-manager/design-system-v2';
import { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import { useMemo, useState } from 'react';
import { usePermissions } from '../../../../hooks/usePermissions';
import { ExpenseCategoryDatabase, ExpenseDatabase } from '../../../../types';
import { getSeriesColor } from '../../utils/chartTheme';
import { getExpenseYear, getMonthlyAmounts, getYearTotals, MONTH_LABELS } from '../../utils/expenseStats';
import { formatAmount } from '../../utils/formatAmount';

type Props = {
  categories: ExpenseCategoryDatabase[];
  expenses: ExpenseDatabase[];
};

const LATEST_EXPENSES_COUNT = 3;

export function useCategoryDetails(props: Props) {
  const { isAdmin } = usePermissions();
  const isCompact = useBreakpointDown('md');
  const [selectedCategoryId, setSelectedCategoryId] = useState(props.categories[0]?.id ?? '');
  const [isExpenseFormOpen, setIsExpenseFormOpen] = useState(false);
  const [isExpenseListOpen, setIsExpenseListOpen] = useState(false);

  const selectedCategory = props.categories.find((category) => category.id === selectedCategoryId);

  const categoryExpenses = useMemo(
    () => props.expenses.filter((expense) => expense.category_id === selectedCategoryId),
    [props.expenses, selectedCategoryId],
  );

  const latestFirst = useMemo(() => [...categoryExpenses].reverse(), [categoryExpenses]);
  const yearTotals = useMemo(() => getYearTotals(categoryExpenses), [categoryExpenses]);

  const chartData: ChartData<'line', (number | null)[], string> = useMemo(() => {
    const years = [...new Set(categoryExpenses.map(getExpenseYear))].sort((a, b) => b - a);

    return {
      labels: MONTH_LABELS,
      datasets: years.map((year, index) => ({
        label: String(year),
        data: getMonthlyAmounts(categoryExpenses, selectedCategoryId, year),
        borderColor: getSeriesColor(index),
        backgroundColor: getSeriesColor(index),
        tension: 0,
        spanGaps: true,
      })),
    };
  }, [categoryExpenses, selectedCategoryId]);

  const chartOptions: ChartOptions<'line'> = {
    responsive: true,
    aspectRatio: isCompact ? 1.2 : 2.4,
    plugins: {
      legend: { position: 'top' },
      tooltip: {
        callbacks: {
          label: (context: TooltipItem<'line'>) => (
            `${context.dataset.label} : ${typeof context.raw === 'number' ? formatAmount(context.raw) : '—'}`
          ),
        },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        grace: '10%',
        ticks: {
          callback: (value) => (typeof value === 'number' ? formatAmount(value) : value),
        },
      },
    },
  };

  return {
    isAdmin,
    selectedCategoryId,
    selectedCategory,
    categoryExpenses,
    latestExpenses: latestFirst.slice(0, LATEST_EXPENSES_COUNT),
    allExpensesLatestFirst: latestFirst,
    hasMoreExpenses: latestFirst.length > LATEST_EXPENSES_COUNT,
    yearTotals,
    chartData,
    chartOptions,
    isExpenseFormOpen,
    isExpenseListOpen,
    handleCategoryChange: setSelectedCategoryId,
    handleOpenExpenseForm: () => setIsExpenseFormOpen(true),
    handleCloseExpenseForm: () => setIsExpenseFormOpen(false),
    handleOpenExpenseList: () => setIsExpenseListOpen(true),
    handleCloseExpenseList: () => setIsExpenseListOpen(false),
  };
}
